import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react'

import { useToast, type ToastKind } from '@/stores/toast'
import { cn } from '@/lib/utils'

const KIND: Record<ToastKind, { box: string; chip: string; bar: string; Icon: typeof Info; label: string }> = {
  success: {
    box:  'bg-emerald-50 border-emerald-200 text-emerald-900',
    chip: 'bg-emerald-100 text-emerald-600',
    bar:  'bg-emerald-400/70',
    Icon: CheckCircle2, label: '완료',
  },
  warn: {
    box:  'bg-amber-50 border-amber-200 text-amber-900',
    chip: 'bg-amber-100 text-amber-600',
    bar:  'bg-amber-400/70',
    Icon: TriangleAlert, label: '확인',
  },
  error: {
    box:  'bg-rose-50 border-rose-200 text-rose-900',
    chip: 'bg-rose-100 text-rose-600',
    bar:  'bg-rose-400/70',
    Icon: XCircle, label: '오류',
  },
  info: {
    box:  'bg-white border-slate-200 text-slate-800',
    chip: 'bg-slate-100 text-slate-500',
    bar:  'bg-slate-300/80',
    Icon: Info, label: '안내',
  },
}

export function ToastContainer() {
  const items = useToast((s) => s.items)
  const remove = useToast((s) => s.remove)

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2.5 pointer-events-none w-[min(92vw,380px)]">
      {items.map((t) => {
        const k = KIND[t.kind]
        const { Icon } = k
        return (
          <div
            key={t.id}
            role="status"
            aria-live="polite"
            className={cn(
              'toast-anim pointer-events-auto relative overflow-hidden rounded-xl border shadow-lg',
              'flex items-start gap-3 pl-3 pr-2.5 py-2.5', k.box,
            )}
          >
            <span className={cn('mt-0.5 grid place-items-center w-6 h-6 rounded-full shrink-0', k.chip)}>
              <Icon className="w-4 h-4" strokeWidth={2.4} />
            </span>
            <div className="min-w-0 flex-1 py-0.5">
              <div className="text-[11px] font-bold uppercase tracking-wide opacity-60 leading-none mb-1">{k.label}</div>
              <div className="text-sm leading-snug break-words whitespace-pre-line">{t.message}</div>
            </div>
            <button
              onClick={() => remove(t.id)}
              aria-label="닫기"
              className="shrink-0 -mr-0.5 rounded-md p-1 opacity-50 hover:opacity-100 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black/10 transition"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <span
              className={cn('toast-bar absolute left-0 bottom-0 h-[3px] rounded-full', k.bar)}
              style={{ animation: `toast-bar ${t.ttl}ms linear forwards` }}
            />
          </div>
        )
      })}
    </div>
  )
}
